import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { getBrand } from '@/config/brands';

// All brands route through the same self-hosted Kong gateway (localhost:8000)
const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJvbGUiOiJhbm9uIiwiaWF0IjoxNzY2ODk0MzkyLCJleHAiOjI3MTI5NzQzOTJ9.eY6l0ZfnsoWUGlMlnmeZnHG7bKpdggO7A8w__InWK1o";

// Dynamic API URL: api.{current_domain} — works for any brand automatically
function getSupabaseUrl(): string {
  if (typeof window !== 'undefined') {
    const { hostname, port, protocol } = window.location;

    // Capacitor native app: protocol is capacitor:// or ionic://
    if (protocol === 'capacitor:' || protocol === 'ionic:') {
      return 'https://api.americanhealthprices.com';
    }

    // Dev: use Vite proxy (forwards /rest/v1 etc. to Kong at localhost:8000)
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return window.location.origin;
    }

    // AHP brand: backend stays on the legacy api.americanhealthprices.com after
    // the 2026-06-18 apex migration (per user decision — emails, static R2, and
    // the Supabase Kong gateway are host-locked and not part of the migration).
    // Without this branch, the dynamic `api.{hostname}` rule below would try
    // api.americanhospitalprices.com which has no DNS / wrong-tenant upstream.
    if (hostname.endsWith('americanhospitalprices.com') || hostname.endsWith('americanhealthprices.com')) {
      return 'https://api.americanhealthprices.com';
    }

    // Cloudflare Pages preview deploys (<hash>.<project>.pages.dev) have no
    // api.* DNS of their own — route by Pages project name so previews can
    // reach the real backend instead of a certless api.*.pages.dev host.
    if (hostname.endsWith('.pages.dev')) {
      if (hostname.includes('luigi-shame')) return 'https://api.luigishallofshame.com';
      if (hostname.includes('antigreed')) return 'https://api.antigreed.app';
      return 'https://api.americanhealthprices.com';
    }

    // Production: strip subdomain prefixes, then prepend "api."
    const baseDomain = hostname.replace(/^(www|dev)\./, '');
    return `${protocol}//api.${baseDomain}`;
  }

  // SSR / build-time fallback. getBrand() picks the right API origin based
  // on VITE_BRAND at build time so an AHP build doesn't accidentally call
  // luigi's gateway from non-browser contexts.
  return import.meta.env.VITE_SUPABASE_URL || getBrand().apiUrl;
}

function getSupabaseKey(): string {
  return import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || SUPABASE_ANON_KEY;
}

// Brand-aware client info header
function getClientInfo(): string {
  const brand = import.meta.env.VITE_BRAND;
  if (brand === 'ahp') return 'american-health-prices';
  if (brand === 'antigreed') return 'antigreed-app';
  return 'luigis-hall-of-shame';
}

const SUPABASE_URL = getSupabaseUrl();
const SUPABASE_KEY = getSupabaseKey();

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_KEY, {
  auth: {
    storage: localStorage,
    persistSession: true,
    autoRefreshToken: true,
  },
  realtime: {
    worker: false,
    params: {
      eventsPerSecond: 0,
    }
  },
  global: {
    headers: {
      'X-Client-Info': getClientInfo()
    }
  }
});